import { ArrowLeft } from "lucide-react";
import { Link, matchPath, useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { Button } from "@/components/ui/button";
import { useGetUserPermissions } from "@/features/account-settings/api/get-user-permissions";
import { BasicUserInformationForm } from "@/features/account-settings/components/basic-user-information.form";
import { UserAvatarForm } from "@/features/account-settings/components/user-avatar.form";
import { UserPermissionsForm } from "@/features/account-settings/components/user-permissions.form";
import { useGetOrganisation } from "@/features/organisations/api/get-organisation";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useGetUser } from "@/features/users/api/get-user";

export function UserPage() {
  const { id } = useParams<{ id: string }>();
  const { organisation } = useOrganisation();
  const {
    data: user,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useGetUser(id ?? "");

  const isSuperAdminAllUsersScreen =
    matchPath("/admin/users/:id", location.pathname) !== null;

  const {
    data: permissions,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
    isSuccess: isPermissionsSuccess,
    error: permissionsError,
  } = useGetUserPermissions(id ?? "", organisation?.id ?? "", {
    enabled: !isSuperAdminAllUsersScreen,
  });

  const {
    data: organisationData,
    isLoading: isOrganisationLoading,
    isSuccess: isOrganisationSuccess,
    isError: isOrganisationError,
    error: organisationError,
  } = useGetOrganisation(
    { id: organisation.id },
    { enabled: !!organisation.id },
  );

  const ReturnButton = () => (
    <Button variant="ghost" asChild className="mb-6 self-start">
      <Link
        to={isSuperAdminAllUsersScreen ? "/admin/users" : "/settings/users"}
      >
        <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
        Return to Users
      </Link>
    </Button>
  );

  if (isLoading || isPermissionsLoading || isOrganisationLoading)
    return (
      <div>
        <ReturnButton />
        <OnboardingHeader title="Edit user" className="mb-6" />
        <LoadingScreen />
      </div>
    );

  const isPermissionShouldError = isSuperAdminAllUsersScreen
    ? false
    : !isPermissionsSuccess || isPermissionsError || isOrganisationError;

  if (
    isError ||
    !isSuccess ||
    isPermissionShouldError ||
    isOrganisationError ||
    !isOrganisationSuccess
  ) {
    return (
      <div>
        <ReturnButton />
        <OnboardingHeader title="Edit User" className="mb-6" />
        <ErrorMessage error={error || permissionsError || organisationError} />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <ReturnButton />
      <OnboardingHeader
        title={`Edit user: ${user.firstName} ${user.lastName}`}
        className="mb-6"
      />
      <BasicUserInformationForm
        initialData={{
          sub: user.id,
          given_name: user.firstName,
          family_name: user.lastName,
          email: user.email,
        }}
        isEditingOwnUser={false}
      />
      <UserAvatarForm userId={user.id} avatarURL={user.avatarURL} />

      {!isSuperAdminAllUsersScreen && (
        <UserPermissionsForm
          initialData={{
            roles: permissions?.roles,
            roleGroup: permissions?.roleGroup,
          }}
          userId={user.id}
          organisationData={organisationData}
        />
      )}
    </section>
  );
}
